import { experimental_streamedQuery as streamedQuery, useQuery } from '@tanstack/react-query';
import {
  streamEquipmentActivity,
  type EquipmentActivityEvent,
  type IEquipmentActivity,
} from '@apis/equipment';

export type { IEquipmentActivity } from '@apis/equipment';

/** The stream's events, then the end of the stream. */
type ActivityChunk = EquipmentActivityEvent | { type: 'end' };

const NO_DAYS: IEquipmentActivity = { utilization: [] };

/**
 * Folds one chunk into the week: a `week` sets it, a `day` fills its slot.
 * At the end, a day still pending was never sent — the stream broke off — so
 * it reads as unavailable rather than loading forever.
 */
export function foldActivity(week: IEquipmentActivity, chunk: ActivityChunk): IEquipmentActivity {
  switch (chunk.type) {
    case 'week':
      return { utilization: chunk.utilization };
    case 'day':
      return { utilization: week.utilization.map((d) => (d.date === chunk.day.date ? chunk.day : d)) };
    case 'end':
      return {
        utilization: week.utilization.map((d) =>
          d.pending ? { date: d.date, activeMinutes: 0, utilizationPct: 0, hasData: false, unavailable: true } : d,
        ),
      };
  }
}

/**
 * The weekly utilization chart's 6 finished days for one asset, streamed: the
 * server's cached days arrive at once (the query is pending until then), each
 * missing day as it loads. Today's bar and the timeline come from
 * useEquipmentTimeline.
 *
 * Only fires when the detail panel is open (both ids present). Closing it
 * aborts the stream, and the server stops fetching the days still missing.
 * staleTime 5 min — archive data doesn't change within a normal session.
 */
export function useEquipmentActivity(
  siteId: string | null,
  equipmentId: string | null,
) {
  return useQuery({
    queryKey: ['equipment', 'activity', siteId, equipmentId],
    queryFn: streamedQuery({
      streamFn: async function* ({ signal }): AsyncGenerator<ActivityChunk> {
        yield* streamEquipmentActivity(siteId!, equipmentId!, signal);
        yield { type: 'end' };
      },
      reducer: foldActivity,
      initialValue: NO_DAYS,
    }),
    enabled: !!siteId && !!equipmentId,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    // A day that timed out upstream won't answer on a retry — fail fast to "unavailable".
    retry: false,
  });
}
